import { Injectable } from "@angular/core";
import { Observable, of, shareReplay, Subject, switchMap } from "rxjs";
import { loadStripe } from "@stripe/stripe-js";
import { map, retry, take, tap } from "rxjs/operators";
import { StoreSelectorsService } from "./store-selectors.service";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { actionSelectedUserUpdate } from "../store/actions/user.actions";
import { IUser } from "../interfaces";
import { ToastrService } from "ngx-toastr";
import _ from "lodash";


@Injectable({
    providedIn: "root",
})
export class FintechService {
    testKey = "pk_test_51PdWcODxKNLWTut5EIvnMa2VvDtG4hmBttOdzlhdIbcgBcBUJ0EBx31SADoK1qJmcyBLVQp6DOHPKx1JWvcBVv5K00yJwOOOFh";
    liveKey = "pk_live_51PdWcODxKNLWTut5fNR9i3anDzgt1zN3UjiBldVjHAhVHtP2xXmMWwSwLekhrzEADv3xL5wOfp4ialm7mpfYckh100H7KNYhUo";
    user: IUser;
    defaultFintech;
    businessId;
    payerId;
    resellerId;
    recurringMode;
    fintechInfoCache$: Observable<any>;
    payerToken$: Observable<any>;
    notify$: Subject<any>;
    busy$: Subject<any>;
    data;
    busy;
    paymentInfo;
    editMode = "info";
    stripe;

    // fintechInfo$;

    constructor(private storeSelector: StoreSelectorsService,
                private http: HttpClient,
                private route: ActivatedRoute,
                private toastr: ToastrService) {

        this.notify$ = new Subject<any>();
        this.busy$ = new Subject<any>();
    }

    private postToFintechWithRetry(endpoint: string, options: any): Observable<any> {
        const url = `https://pay.signage.me:8443/fintech/${endpoint}`;
        return this.http.post(url, options).pipe(
            retry({
                count: 5,
                delay: (error, retryCount) => {
                    this.toastr.info(`Please wait connecting to server... (${retryCount})`, `FinTech`, { timeOut: 3000 });
                    return of(error);
                },
            }),
        );
    }

    setBusy(busy) {
        this.busy = busy;
        this.busy$.next(busy);
    }

    getPayerToken(): Observable<any> {
        if (this.payerToken$ != null) {
            return this.payerToken$;
        }

        const payerToken = this.route.snapshot.paramMap.get("encrypted_id");
        if (payerToken != null) {
            this.payerToken$ = of(payerToken);
        } else {
            this.payerToken$ = of("token").pipe(
                switchMap(a => this.storeSelector.getUser()),
                take(1),
                switchMap(user => {
                    this.user = user;
                    const options = {
                        customerUser: user.email,
                        customerPass: user.passwordNew,
                    };
                    const url = "https://galaxy.signage.me/Web/getPayerToken.ashx";
                    return this.http.post(url, options);
                }),
                map(reply => (reply as any).token),
                shareReplay(1),
            );
        }
        return this.payerToken$;
    }


    load(i_data): Observable<any> {
        if (i_data.status == "fail") {
            console.error("error in server: " + i_data.error);
        }
        this.data = i_data;
        this.defaultFintech = i_data.defaultFintech;
        this.businessId = i_data.businessId;
        this.payerId = i_data.payerId;
        this.resellerId = i_data.resellerId;
        this.recurringMode = i_data.recurringMode;


        const publishedKey = i_data.sandbox ? this.testKey : this.liveKey;
        if (this.stripe != null) {
            return of(this.stripe);
        } else {
            return new Observable(sub => {
                loadStripe(publishedKey).then(stripe => {
                    this.stripe = stripe;
                    sub.next(stripe);
                    sub.complete();
                });
            });
        }
    }

    getFintechInfo(): Observable<any> {
        if (this.fintechInfoCache$) {
            return this.fintechInfoCache$;
        }
        this.fintechInfoCache$ = this.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.postToFintechWithRetry("getFintechInfo", { token: payerToken });
            }),
            switchMap(ret => this.load(ret)),
            tap(a => {
                this.notify$.next("update");
            }),
            shareReplay(1),
        );
        return this.fintechInfoCache$;
    }

    selectPayMethod(i_recurringPayMethodId, i_orderPayMethodId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const options = {
                    token: payerToken,
                    recurringPayMethodId: i_recurringPayMethodId,
                    orderPayMethodId: i_orderPayMethodId,
                };
                return this.postToFintechWithRetry("selectPayMethod", options);
            }),
        );
    }

    deletePayMethod(i_payMethodId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const options = { token: payerToken, payMethodId: i_payMethodId };
                return this.postToFintechWithRetry("deletePayMethod", options);
            }),
        );
    }

    updateAccountStatus(i_status) {
        if (this.user != null) {
            const usr = _.cloneDeep(this.user);
            usr.accountStatusReseller = i_status;
            this.storeSelector.dispatch(actionSelectedUserUpdate({ user: usr }));
            this.toastr.clear();
        }
    }

    unsubscribe() {
        return this.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.postToFintechWithRetry("unsubscribe", { token: payerToken });
            }),
        );
    }

    getOrderInfo(i_orderId): Observable<any> {
        return this.getPayerToken().pipe(
            switchMap(payerToken => {
                const options = { token: payerToken, orderId: i_orderId };
                return this.postToFintechWithRetry("getOrderInfo", options);
            }),
        );
    }

    createOrderInfo(i_orderId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const options = { token: payerToken, orderId: i_orderId };
                return this.postToFintechWithRetry("createOrderInfo", options);
            }),
        );
    }

    updateOrderInfo(orderInfo): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                orderInfo.token = payerToken;
                return this.postToFintechWithRetry("updateOrderInfo", orderInfo);
            }),
        );
    }
}
